import React, { useState } from 'react'
import { Feature } from '../../api/client'
import { ReferenceSelector } from './ReferenceSelector'
import { useCollection } from '../../hooks/useCollection'
import { collectionItemNoun, renderCollectionItem } from '../../utils/collectionUtils'
import { useCollectionItem } from '../../hooks/useCollectionItem'
import { ItemListEditorShell } from './ItemListEditorShell'
import PersonAvatar from '../PersonAvatar'
import PersonDetailModal from './PersonDetailModal'
import EditCollectionItemById from '../collections/EditCollectionItemById'
import { PersonLikeData } from '../../utils/people'

interface ReferenceListEditorProps {
    feature: Feature
    values: Array<{
        id?: string
        value: number  // collection item id
    }>
    onChange: (values: Array<{ id?: string; value: number }>) => void
    onClose: () => void
    /** Page-level collection-item cache (id → item with its `collection`
     *  attached) — the same map the read-only chips render from. Rows seed
     *  their display from it so opening the editor shows real names
     *  immediately instead of flashing `#id` while per-row fetches land. */
    collectionItems?: Record<string, any>
}

type Row = { id?: string; value: number }

/**
 * Thin wrapper over `ItemListEditorShell` for reference-typed lists. The
 * shell owns local state, save/cancel/Esc/click-outside, and the visual
 * frame; this component supplies the per-row display (the collection item)
 * and the "+ Add" trigger (a button that opens `ReferenceSelector`).
 */
export const ReferenceListEditor: React.FC<ReferenceListEditorProps> = ({
    feature,
    values,
    onChange,
    onClose,
    collectionItems,
}) => {
    const collectionId = feature.config?.params?.collection_id || 0
    const { data: fetchedCollection } = useCollection(collectionId)
    // Cached items arrive with their collection attached (`{name, config,
    // system_kind}` from the checkins response), so the very first paint can
    // already render display templates and know people-ness; the fetched
    // copy wins once it lands.
    const cachedCollection = values
        .map((v) => collectionItems?.[String(v.value)]?.collection)
        .find(Boolean)
    const collection = fetchedCollection ?? cachedCollection
    const isPeople = collection?.system_kind === 'people'

    // ENFORCE_AS_SET_PLAN Stage 2: when the feature disallows repetitions,
    // reject a second add of the same collection item. Reference identity is
    // the item id, mirroring `_row_value_signature`'s `reference_value`.
    const enforceSet = feature.config?.enforce_as_set === true

    const initial: Row[] = values.map((v, i) => ({ id: v.id || `item-${i}`, value: v.value }))

    return (
        <ItemListEditorShell<Row>
            feature={feature}
            initialItems={initial}
            renderRow={(row) =>
                isPeople ? (
                    <PersonReferenceRow
                        collectionId={collectionId}
                        itemId={row.value}
                        collection={collection}
                        cachedItem={collectionItems?.[String(row.value)]}
                    />
                ) : (
                    <ReferenceItemDisplay
                        collectionId={collectionId}
                        itemId={row.value}
                        collection={collection}
                        cachedItem={collectionItems?.[String(row.value)]}
                    />
                )
            }
            renderAddTrigger={(onAdd) => (
                <AddReferenceTrigger
                    collectionId={collectionId}
                    noun={collectionItemNoun(collection)}
                    onAdd={(itemId) => onAdd({ id: `item-${Date.now()}`, value: itemId })}
                />
            )}
            onChange={onChange}
            onClose={onClose}
            isDuplicate={
                enforceSet
                    ? (candidate, others) => others.some((o) => o.value === candidate.value)
                    : undefined
            }
        />
    )
}

/** Pulsing muted bar sized like a name — the loading state for a row whose
 *  item isn't in the page cache yet (e.g. created in another session since
 *  page load). Never `#id`: that remains only as the terminal fallback after
 *  a failed fetch. */
const NameSkeleton: React.FC = () => (
    <span
        data-testid="ref-row-skeleton"
        className="inline-block h-4 w-24 max-w-full animate-pulse rounded bg-gray-200 align-middle"
    />
)

const ReferenceItemDisplay: React.FC<{
    collectionId: number
    itemId: number
    collection: any
    cachedItem?: any
}> = ({ collectionId, itemId, collection, cachedItem }) => {
    // Seed from the page-level cache when present; keep the per-row GET as a
    // background refresh (the cache can lag a recent edit — same staleness
    // concern documented in `PersonChips`), updating in place when it lands.
    const { item: fetched, error } = useCollectionItem(collectionId, String(itemId))
    const item = fetched ?? cachedItem
    const coll = collection ?? cachedItem?.collection
    if (item && coll) return <span>{renderCollectionItem(item, coll) || `#${itemId}`}</span>
    if (error) return <span>#{itemId}</span>
    return <NameSkeleton />
}

const PersonReferenceRow: React.FC<{
    collectionId: number
    itemId: number
    collection: any
    cachedItem?: any
}> = ({ collectionId, itemId, collection, cachedItem }) => {
    // Same cache-seed + background-refresh pattern as `ReferenceItemDisplay`.
    const { item: fetched, error, refetch } = useCollectionItem(collectionId, String(itemId))
    const item = fetched ?? cachedItem
    const coll = collection ?? cachedItem?.collection
    const [open, setOpen] = useState(false)
    const [editing, setEditing] = useState(false)
    const data = (item?.data || {}) as PersonLikeData
    // Match the chip-strip on the read-only checkin (which goes through
    // `ReferenceValueDisplay`) so editing a People list reads the same
    // names — drives off the collection's `display_template` (today
    // `{first name}`) rather than the first+last formatter.
    const resolved = Boolean(item && coll)
    const label = resolved ? renderCollectionItem(item, coll) || `#${itemId}` : `#${itemId}`
    if (!resolved && !error) {
        return (
            <div className="flex items-center gap-2 -mx-2 -my-1 px-2 py-1">
                <span className="h-5 w-5 flex-shrink-0 rounded-full bg-gray-200 animate-pulse" />
                <NameSkeleton />
            </div>
        )
    }
    return (
        <>
            <div
                role="button"
                tabIndex={0}
                onClick={() => setOpen(true)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setOpen(true)
                    }
                }}
                className="flex items-center gap-2 cursor-pointer rounded hover:bg-gray-50 -mx-2 -my-1 px-2 py-1"
            >
                <PersonAvatar
                    data={data}
                    className="h-5 w-5 flex-shrink-0"
                    initialsClassName="text-[9px]"
                    alt={label}
                />
                <span className="truncate">{label}</span>
            </div>
            {/* Both modals live inside `ItemListEditorShell`, whose Esc
                handler dismisses the whole list editor. Keep Esc from
                reaching it so a stray keypress in the edit form doesn't
                take the form (and its unsaved edits) down with it. */}
            <div onKeyDown={(e) => { if (e.key === 'Escape') e.stopPropagation() }}>
                <PersonDetailModal
                    open={open}
                    value={open ? { collectionItemId: itemId, data } : null}
                    onEdit={() => {
                        setOpen(false)
                        setEditing(true)
                    }}
                    onClose={() => setOpen(false)}
                />
                {editing && (
                    <EditCollectionItemById
                        collectionId={collectionId}
                        itemId={itemId}
                        onClose={() => setEditing(false)}
                        onSuccess={() => {
                            setEditing(false)
                            // No page-level cache behind this row — it
                            // fetches itself, so pull the saved row back in
                            // directly.
                            refetch()
                        }}
                    />
                )}
            </div>
        </>
    )
}

const AddReferenceTrigger: React.FC<{
    collectionId: number
    noun: string
    onAdd: (itemId: number) => void
}> = ({ collectionId, noun, onAdd }) => {
    const [showSelector, setShowSelector] = useState(false)
    return (
        <>
            <button
                onClick={() => setShowSelector(true)}
                className="w-full px-3 py-2 text-sm border border-dashed border-gray-300 rounded text-gray-600 hover:border-amber-300 hover:text-amber-900"
            >
                + Add {noun}
            </button>
            {showSelector && (
                <div className="absolute inset-0">
                    <ReferenceSelector
                        collectionId={collectionId}
                        value={undefined}
                        onChange={(itemId) => {
                            if (itemId) {
                                onAdd(itemId)
                            }
                            setShowSelector(false)
                        }}
                        onClose={() => setShowSelector(false)}
                    />
                </div>
            )}
        </>
    )
}
